import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DataTable,
  Avatar,
  Search,
  IconButton,
  Button,
  InlineAlert,
  Checkbox,
  CheckboxField,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@kuda/ui";
import { PlusIcon, ArrowsRotateIcon, CopyIcon, TagIcon, TrashIcon } from "@kuda/ui/icons";
import { useMsal } from "@azure/msal-react";
import SelectCluster from "../components/selectCluster";
import AwsProfileSelector from "../components/AwsProfileSelector";
import Loading from "../components/loading";
import Dialogbox from "../components/dialogs/dialogBox";
import RenameSecretDialog from "../components/dialogs/renameSecretDialog";
import DuplicateSecretDialog from "../components/dialogs/duplicateSecretDialog";
import BulkRetagDialog from "../components/dialogs/bulkRetagDialog";
import BulkCloneDialog from "../components/dialogs/bulkCloneDialog";
import TimeFormater from "../components/dialogs/timeFormater";
import { useDebounce } from "../components/hooks/useDebounce";
import { useSecrets } from "../components/hooks/useSecrets";
import { useSecretActions } from "../components/hooks/useSecretActions";
import { getTagValue } from "../components/utils/tagHelpers";
import { config, getClusterProviders } from "../components/config";
import useAccessToken from "../auth/accessPermission";
import useUserAccount from "../components/hooks/userAccountInfo";

const PAGE_SIZE = 25;

// Provider badge tokens
const PROVIDER_BADGE_CLASS = {
  aws:   "bg-color-bg-decorative-yellow text-color-text-accent-orange",
  azkv:  "bg-color-bg-decorative-blue text-color-text-state-info-default",
  vault: "bg-color-bg-decorative-purple text-color-text-brand-primary-default",
};

function SecretList() {
  const navigate     = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);
  const [page, setPage] = useState(1);
  const location     = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialCluster = searchParams.get("selectedCluster") || "kuda-devops-eks";

  const [secrets, setSecrets] = useState([]);
  const { instance } = useMsal();

  const searchKey = `secretSearch:${initialCluster}`;
  const [searchText, setSearchText] = useState(() => sessionStorage.getItem(searchKey) || "");
  const debouncedSearchText = useDebounce(searchText, 300);

  const [selectedCluster, setSelectedCluster] = useState(initialCluster);

  // Derive initial provider from cluster config using the new helper
  const initialProviders = getClusterProviders(initialCluster);
  const [availableProviders, setAvailableProviders] = useState(initialProviders);
  const [platformName, setPlatformName] = useState(initialProviders[0]?.platformName || "aws");
  const [vaultId, setVaultId]           = useState(initialProviders[0]?.vaultId || "");

  const urlAwsProfile = searchParams.get("awsProfile");
  const [awsProfile, setAwsProfile] = useState(() => {
    if (urlAwsProfile && config.awsProfiles[urlAwsProfile]) return urlAwsProfile;
    return "profile-a";
  });

  // Keep vaultId in sync with AWS profile
  useEffect(() => {
    if (platformName === config.platforms[0] && config.awsProfiles[awsProfile]) {
      const profileRegion = config.awsProfiles[awsProfile].region;
      setVaultId((prev) => (prev === profileRegion ? prev : profileRegion));
    }
  }, [platformName, awsProfile]);

  const accessToken = useAccessToken([config.apiAccess]);
  const { roles, nameParts } = useUserAccount(instance);
  const { loading, fetchSecrets, deleteSecret } = useSecrets();
  const { duplicateSecret, renameSecret, bulkUpdateTags } = useSecretActions();

  const isDataReady = !!accessToken && roles.length > 0 && getClusterProviders(selectedCluster).length > 0 && !loading;

  // Row selection (bulk delete / re-tag / clone)
  const [selectedNames, setSelectedNames] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkRetagOpen, setBulkRetagOpen] = useState(false);
  const [bulkCloneOpen, setBulkCloneOpen] = useState(false);
  const [bulkResultOpen, setBulkResultOpen] = useState(false);
  const [bulkResultMessage, setBulkResultMessage] = useState("");

  // Duplicate / rename
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [duplicateSuccess, setDuplicateSuccess] = useState(null); // { newName, targetCluster, targetProvider }
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameLoading, setRenameLoading] = useState(false);
  const [renamePartialOpen, setRenamePartialOpen] = useState(false);
  const [renamePartialMessage, setRenamePartialMessage] = useState("");
  const [actionErrorOpen, setActionErrorOpen] = useState(false);
  const [actionError, setActionError] = useState("");

  const buildHeadersForProvider = useCallback(
    (p) => {
      const h = {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        Roles: roles?.join(",") ?? "",
      };
      if (p.platformName === config.platforms[0]) {
        const profileConfig = config.awsProfiles[awsProfile];
        h.smRegion  = profileConfig?.region || p.vaultId;
        h.smAccount = profileConfig?.account || "";
      } else {
        h.vaultName = p.vaultId;
      }
      return h;
    },
    [accessToken, roles, awsProfile]
  );

  const getProviderForRow = useCallback(
    (row) => {
      const providers = getClusterProviders(selectedCluster);
      const target = row.provider || platformName;
      return providers.find((p) => p.platformName === target) || providers[0];
    },
    [selectedCluster, platformName]
  );

  // Navigate to view secret
  const handleNameCellClick = (row) => {
    // Use the provider tagged on the secret itself — not the cluster's default provider
    const secretProvider = row.provider || platformName;
    const currentProviders = getClusterProviders(selectedCluster);
    const currentProvider  = currentProviders.find((p) => p.platformName === secretProvider)
                          || currentProviders[0];

    if (!currentProvider) {
      alert("Error: Missing cluster configuration. Please select a cluster and try again.");
      return;
    }

    let currentVaultId = currentProvider.vaultId;
    if (currentProvider.platformName === config.platforms[0] && config.awsProfiles[awsProfile]) {
      currentVaultId = config.awsProfiles[awsProfile].region;
    }

    const urlParams = new URLSearchParams({
      platformName:    currentProvider.platformName,
      selectedCluster,
      vaultName:       currentVaultId,
    });
    if (currentProvider.platformName === config.platforms[0] && awsProfile) {
      urlParams.set("awsProfile", awsProfile);
    }
    navigate(`/view-secret/${row.name}?${urlParams.toString()}`);
  };

  // Duplicate — opens a dialog so the target cluster/provider, environment,
  // and team can all be chosen (they don't have to match the source).
  const handleOpenDuplicate = useCallback((row) => setDuplicateTarget(row), []);

  const handleConfirmDuplicate = useCallback(
    async ({ newName, targetCluster, targetProvider, environment, team }) => {
      const sourceProvider = getProviderForRow(duplicateTarget);
      const targetProviderObj =
        getClusterProviders(targetCluster).find((p) => p.platformName === targetProvider)
        || { platformName: targetProvider, vaultId: "" };

      await duplicateSecret({
        sourceName: duplicateTarget.name,
        sourcePlatformName: sourceProvider.platformName,
        sourceHeaders: buildHeadersForProvider(sourceProvider),
        newName,
        targetPlatformName: targetProviderObj.platformName,
        targetHeaders: buildHeadersForProvider(targetProviderObj),
        environment,
        team,
        nameParts,
      });

      setDuplicateTarget(null);
      setDuplicateSuccess({ newName, targetCluster, targetProvider: targetProviderObj.platformName });

      // Only the currently-viewed cluster's table needs a refetch, and only after
      // giving AWS/Vault a moment for eventual consistency — mirrors the same
      // delay CreateSecretForm already uses after a create.
      if (targetCluster === selectedCluster) {
        setTimeout(() => setRefreshKey((k) => k + 1), 1500);
      }
    },
    [duplicateTarget, getProviderForRow, buildHeadersForProvider, duplicateSecret, nameParts, selectedCluster]
  );

  const handleViewDuplicate = useCallback(() => {
    if (!duplicateSuccess) return;
    const { newName, targetCluster, targetProvider } = duplicateSuccess;
    const targetProviderObj = getClusterProviders(targetCluster).find((p) => p.platformName === targetProvider);
    let vaultName = targetProviderObj?.vaultId || "";
    if (targetProvider === config.platforms[0] && config.awsProfiles[awsProfile]) {
      vaultName = config.awsProfiles[awsProfile].region;
    }
    const params = new URLSearchParams({ platformName: targetProvider, selectedCluster: targetCluster, vaultName });
    if (targetProvider === config.platforms[0]) params.set("awsProfile", awsProfile);
    setDuplicateSuccess(null);
    navigate(`/view-secret/${newName}?${params.toString()}`);
  }, [duplicateSuccess, navigate, awsProfile]);

  const handleOpenRename = useCallback((row) => setRenameTarget(row), []);

  const handleConfirmRename = useCallback(
    async (newName) => {
      if (!renameTarget) return;
      const provider = getProviderForRow(renameTarget);
      setRenameLoading(true);
      try {
        const result = await renameSecret({
          oldName: renameTarget.name,
          newName,
          platformName: provider.platformName,
          headers: buildHeadersForProvider(provider),
          nameParts,
        });
        setRenameTarget(null);
        // Same eventual-consistency delay as duplicate/create.
        setTimeout(() => setRefreshKey((k) => k + 1), 1500);
        if (result.status === "partial") {
          setRenamePartialMessage(
            `"${newName}" was created, but the old secret "${renameTarget.name}" could not be deleted. Please delete it manually.`
          );
          setRenamePartialOpen(true);
        }
      } catch (err) {
        setActionError(err.response?.data?.error || `Failed to rename "${renameTarget.name}".`);
        setActionErrorOpen(true);
      } finally {
        setRenameLoading(false);
      }
    },
    [renameTarget, getProviderForRow, buildHeadersForProvider, renameSecret, nameParts]
  );

  const handleToggleRow = useCallback((name, checked) => {
    setSelectedNames((prev) => {
      const next = new Set(prev);
      if (checked) next.add(name);
      else next.delete(name);
      return next;
    });
  }, []);

  const handleBulkDelete = useCallback(async () => {
    setBulkDeleteLoading(true);
    try {
      const rowsToDelete = secrets.filter((r) => selectedNames.has(r.name));
      const results = await Promise.allSettled(
        rowsToDelete.map((row) => {
          const provider = getProviderForRow(row);
          return deleteSecret(row.name, provider.platformName, buildHeadersForProvider(provider));
        })
      );
      const failures = results.filter((r) => r.status === "rejected");
      setBulkDeleteOpen(false);
      setSelectedNames(new Set());
      setRefreshKey((k) => k + 1);
      if (failures.length) {
        const reasons = [
          ...new Set(
            failures.map((f) => f.reason?.response?.data?.error || f.reason?.message || "Unknown error")
          ),
        ];
        setActionError(
          `${failures.length} of ${rowsToDelete.length} secret(s) failed to delete: ${reasons.join("; ")}`
        );
        setActionErrorOpen(true);
      }
    } finally {
      setBulkDeleteLoading(false);
    }
  }, [secrets, selectedNames, getProviderForRow, buildHeadersForProvider, deleteSecret]);

  const handleConfirmBulkRetag = useCallback(
    async ({ environment, team }) => {
      const rows = secrets.filter((r) => selectedNames.has(r.name));
      const results = await Promise.allSettled(
        rows.map((row) => {
          const provider = getProviderForRow(row);
          return bulkUpdateTags({
            secretName: row.name,
            platformName: provider.platformName,
            headers: buildHeadersForProvider(provider),
            environment,
            team,
            nameParts,
          });
        })
      );
      const failures = results.filter((r) => r.status === "rejected");
      setBulkRetagOpen(false);
      setSelectedNames(new Set());
      setTimeout(() => setRefreshKey((k) => k + 1), 1500);

      if (failures.length) {
        const reasons = [
          ...new Set(failures.map((f) => f.reason?.response?.data?.error || f.reason?.message || "Unknown error")),
        ];
        setActionError(`${failures.length} of ${rows.length} secret(s) failed to update: ${reasons.join("; ")}`);
        setActionErrorOpen(true);
      } else {
        setBulkResultMessage(`Updated tags on ${rows.length} secret${rows.length === 1 ? "" : "s"}.`);
        setBulkResultOpen(true);
      }
    },
    [secrets, selectedNames, getProviderForRow, buildHeadersForProvider, bulkUpdateTags, nameParts]
  );

  const handleConfirmBulkClone = useCallback(
    async ({ targetCluster, targetProvider }) => {
      const rows = secrets.filter((r) => selectedNames.has(r.name));
      const targetProviderObj =
        getClusterProviders(targetCluster).find((p) => p.platformName === targetProvider)
        || { platformName: targetProvider, vaultId: "" };
      const targetHeaders = buildHeadersForProvider(targetProviderObj);

      // Some Vault engines store secrets under nested <environment>/<team>/<name>
      // paths — cloning a flat AWS/Azure name (or another vault path) straight
      // across would land it at the mount root instead. Derive the same way
      // CreateSecretForm/DuplicateSecretDialog do, from each row's own tags.
      const usesNestedVaultPath =
        targetProviderObj.platformName === "vault" &&
        config.vaultNestedPathEngines.includes(targetProviderObj.vaultId);

      const results = await Promise.allSettled(
        rows.map((row) => {
          const sourceProvider = getProviderForRow(row);
          const environment = getTagValue(row.tags, "Environment");
          const team = getTagValue(row.tags, "Project");
          const leafName = row.name.split("/").pop();
          const newName = usesNestedVaultPath ? `${environment}/${team}/${leafName}` : row.name;
          return duplicateSecret({
            sourceName: row.name,
            sourcePlatformName: sourceProvider.platformName,
            sourceHeaders: buildHeadersForProvider(sourceProvider),
            newName,
            targetPlatformName: targetProviderObj.platformName,
            targetHeaders,
            environment,
            team,
            nameParts,
          });
        })
      );
      const failures = results.filter((r) => r.status === "rejected");
      setBulkCloneOpen(false);
      setSelectedNames(new Set());
      if (targetCluster === selectedCluster) {
        setTimeout(() => setRefreshKey((k) => k + 1), 1500);
      }

      if (failures.length) {
        const reasons = [
          ...new Set(failures.map((f) => f.reason?.response?.data?.error || f.reason?.message || "Unknown error")),
        ];
        setActionError(`${failures.length} of ${rows.length} secret(s) failed to clone: ${reasons.join("; ")}`);
        setActionErrorOpen(true);
      } else {
        setBulkResultMessage(`Cloned ${rows.length} secret${rows.length === 1 ? "" : "s"} into "${targetCluster}".`);
        setBulkResultOpen(true);
      }
    },
    [secrets, selectedNames, getProviderForRow, buildHeadersForProvider, duplicateSecret, nameParts, selectedCluster]
  );

  // Table columns — same as original + Provider + Last Updated By + selection/actions
  const columns = useMemo(
    () => [
      {
        key: "select",
        header: "",
        width: 40,
        render: (_value, row) => (
          <Checkbox
            selected={selectedNames.has(row.name)}
            onCheckedChange={(checked) => handleToggleRow(row.name, checked === true)}
            aria-label={`Select ${row.name}`}
          />
        ),
      },
      { key: "id", header: "ID", width: 80 },
      {
        key: "name",
        header: "Name",
        width: "22%",
        // Vault names can be full nested paths (dev/kuda-internaltools/foo) —
        // row.name (used for the view-secret link / row identity) keeps the
        // full path, only the visible label is trimmed to the leaf segment.
        render: (value, row) => (
          <button
            type="button"
            onClick={() => handleNameCellClick(row)}
            title={value}
            className="font-semibold text-color-text-brand-primary-default"
            style={{ cursor: "pointer", background: "none", border: "none", padding: 0, textAlign: "left" }}
          >
            {value?.split("/").pop()}
          </button>
        ),
      },
      {
        key: "provider",
        header: "Provider",
        width: 110,
        render: (_value, row) => {
          const p     = (row.provider || platformName).toLowerCase();
          const label = (row.provider || platformName).toUpperCase();
          const badgeClass = PROVIDER_BADGE_CLASS[p] || PROVIDER_BADGE_CLASS.vault;
          return (
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold ${badgeClass}`}>
              {label}
            </span>
          );
        },
      },
      {
        key: "Environment",
        header: "Environment",
        width: 120,
        render: (_value, row) => getTagValue(row.tags, "Environment"),
      },
      {
        key: "Project",
        header: "Team",
        width: 100,
        render: (_value, row) => getTagValue(row.tags, "Project"),
      },
      {
        key: "updatedBy",
        header: "Last Updated By",
        width: 170,
        render: (_value, row) => {
          const name = getTagValue(row.tags, "UpdatedBy") || "—";
          return (
            <div className="flex items-center gap-2">
              {name !== "—" && <Avatar type="Initial" size="sm" initialLabel={name} />}
              <div>
                <div className="text-sm font-semibold text-color-text-primary">{name}</div>
                <TimeFormater timestamp={row.updated_on} />
              </div>
            </div>
          );
        },
      },
      {
        key: "actions",
        header: "",
        width: 90,
        render: (_value, row) => (
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  fill="Transparent"
                  size="xs"
                  onClick={() => handleOpenDuplicate(row)}
                  icon={<CopyIcon className="size-4" />}
                  aria-label="Duplicate"
                />
              </TooltipTrigger>
              <TooltipContent>Duplicate</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  fill="Transparent"
                  size="xs"
                  onClick={() => handleOpenRename(row)}
                  icon={<TagIcon className="size-4" />}
                  aria-label="Rename"
                />
              </TooltipTrigger>
              <TooltipContent>Rename</TooltipContent>
            </Tooltip>
          </div>
        ),
      },
    ],
    [selectedNames, handleToggleRow, platformName, handleOpenDuplicate, handleOpenRename]
  );

  // Filtered rows
  const filteredRows = useMemo(() => {
    if (!debouncedSearchText) return secrets;
    return secrets.filter((row) =>
      columns.some((column) => {
        const value = row[column.key];
        return value && value.toString().toLowerCase().includes(debouncedSearchText.toLowerCase());
      })
    );
  }, [secrets, debouncedSearchText, columns]);

  const pagedRows = useMemo(
    () => filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredRows, page]
  );

  useEffect(() => setPage(1), [debouncedSearchText, selectedCluster]);

  // Process project string for team avatar — kept from original
  const processProjectString = useCallback((projectStr) => {
    if (!projectStr || typeof projectStr !== "string") return { initials: "", displayName: "" };
    const stopWords = ["and", "or", "the", "of", "in", "at", "to", "for", "with"];
    const allWords  = projectStr.split("-").map((w) => w.trim()).filter((w) => w.length > 0);
    const initials  = allWords
      .filter((w) => !stopWords.includes(w.toLowerCase()))
      .map((w) => w[0].toUpperCase())
      .join("");
    const displayName = allWords.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    return { initials, displayName };
  }, []);

  const handleEditClick = useCallback(() => {
    const profileParam = platformName === config.platforms[0] ? `?awsProfile=${awsProfile}` : "";
    navigate(`/create-secret${profileParam}`);
  }, [navigate, platformName, awsProfile]);

  const handleClusterChange = useCallback(
    (cluster) => {
      setSelectedCluster(cluster);
      const providers = getClusterProviders(cluster);
      const first     = providers[0] || { platformName: "aws", vaultId: "" };
      setPlatformName(first.platformName);
      setVaultId(first.vaultId);
      setAvailableProviders(providers);
      setSelectedNames(new Set());

      const savedSearch = sessionStorage.getItem(`secretSearch:${cluster}`) || "";
      setSearchText(savedSearch);

      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set("selectedCluster", cluster);
      navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
    },
    [navigate, location]
  );

  const handleAwsProfileChange = useCallback(
    (profile) => {
      setAwsProfile(profile);
      if (platformName === config.platforms[0]) {
        const profileConfig = config.awsProfiles[profile];
        if (profileConfig) setVaultId(profileConfig.region);
      }
    },
    [platformName]
  );

  const handleSearch = useCallback(
    (event) => {
      const value = event.target.value;
      setSearchText(value);
      sessionStorage.setItem(`secretSearch:${selectedCluster}`, value);
    },
    [selectedCluster]
  );

  const { initials, displayName } = useMemo(
    () =>
      processProjectString(
        roles.find(
          (r) => !config.rbac.adminRoles.includes(r) && !config.rbac.elevatedRoles.includes(r)
        ) || ""
      ),
    [roles, processProjectString]
  );

  // Fetch secrets from ALL providers for the selected cluster and merge
  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentAccount = instance.getActiveAccount();
        if (!currentAccount || !accessToken || roles.length === 0) return;

        const providers = getClusterProviders(selectedCluster);
        if (!providers.length) return;

        // Fetch all providers in parallel
        const results = await Promise.allSettled(
          providers.map(async (p) => {
            const data = await fetchSecrets(p.platformName, buildHeadersForProvider(p));
            // Tag each secret with its provider
            return data.map((s) => ({ ...s, provider: p.platformName }));
          })
        );

        // Merge successful results, ignore failed ones
        const merged = results
          .filter((r) => r.status === "fulfilled")
          .flatMap((r) => r.value)
          .map((s, idx) => ({ ...s, id: idx + 1 }));

        setSecrets(merged);
      } catch (error) {
        console.error("Error fetching secrets:", error);
      }
    };

    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    instance,
    JSON.stringify(roles),
    accessToken,
    selectedCluster,
    awsProfile,
    location.key,
    refreshKey,
  ]);

  if (!isDataReady) {
    return (
      <div className="flex flex-col items-center" style={{ minHeight: "100vh" }}>
        <div style={{ width: "80%" }}>
          <Loading open type="skeleton" variant="table" rows={10} width="100%" height="85vh" position="center" />
        </div>
      </div>
    );
  }

  const currentProfileInfo = config.awsProfiles[awsProfile];
  const showProfileInfo    = platformName === config.platforms[0] && currentProfileInfo;
  const allSelected = filteredRows.length > 0 && selectedNames.size === filteredRows.length;
  const someSelected = selectedNames.size > 0 && !allSelected;

  return (
    <div className="flex flex-col items-center" style={{ minHeight: "100vh" }}>
      <div style={{ width: "80%" }}>

        {/* Page title */}
        <div className="mt-1 mb-1">
          <h1 className="font-heading text-xl font-bold text-color-text-brand-primary-default">Secrets</h1>
          <p className="text-xs text-color-text-secondary">
            Manage and view secrets across all clusters and providers
          </p>
        </div>

        {/* Active AWS profile banner — kept from original */}
        {showProfileInfo && (
          <div className="mb-4">
            <InlineAlert
              type="Info"
              showTitle={false}
              subtext={`Active AWS Profile: ${currentProfileInfo.name} | ${currentProfileInfo.region}`}
              width="100%"
            />
          </div>
        )}

        {/* Toolbar */}
        <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
          {/* Team avatar — kept from original */}
          {displayName && (
            <div className="flex items-center gap-4 mt-5">
              <Avatar entity="Business" type="Initial" size="xl" initialLabel={initials} />
              <span className="font-heading text-lg font-bold">{displayName}</span>
            </div>
          )}

          {/* Cluster selector */}
          <SelectCluster
            onClusterChange={handleClusterChange}
            style={{ width: "30ch", marginTop: "1.25rem" }}
            label="Current Cluster"
            defaultCluster={selectedCluster}
            showAll={true}
            userRole={roles}
          />

          {/* AWS profile selector */}
          <AwsProfileSelector
            selectedProfile={awsProfile}
            onProfileChange={handleAwsProfileChange}
            platformName={platformName}
          />

          {/* Search */}
          <div className="mt-5" style={{ width: 220 }}>
            <Search
              variant="standalone"
              placeholder="Filter by name"
              value={searchText}
              onChange={handleSearch}
            />
          </div>

          {/* Refresh button */}
          <IconButton
            fill="Transparent"
            onClick={() => setRefreshKey((k) => k + 1)}
            icon={<ArrowsRotateIcon className="size-4" />}
            aria-label="Refresh list"
            className="mt-5"
          />

          {/* Create Secret button — outlined to match cluster selector style */}
          <Button variant="outlined" onClick={handleEditClick} className="mt-5">
            <PlusIcon className="size-4" /> Create Secret
          </Button>
        </div>

        {/* Selection bar — appears once rows are available */}
        {filteredRows.length > 0 && (
          <div className="flex items-center gap-3 mb-2">
            <CheckboxField
              variant="unbounded"
              label={`Select all (${filteredRows.length})`}
              selected={allSelected}
              selectedType={someSelected ? "dash" : "check"}
              onCheckedChange={(checked) =>
                setSelectedNames(checked === true ? new Set(filteredRows.map((r) => r.name)) : new Set())
              }
            />
            {selectedNames.size > 0 && (
              <>
                <span className="text-sm text-color-text-secondary">{selectedNames.size} selected</span>
                <Button variant="outlined" size="sm" onClick={() => setBulkRetagOpen(true)}>
                  <TagIcon className="size-4" /> Edit tags
                </Button>
                <Button variant="outlined" size="sm" onClick={() => setBulkCloneOpen(true)}>
                  <CopyIcon className="size-4" /> Clone to cluster
                </Button>
                <Button variant="destructive" size="sm" onClick={() => setBulkDeleteOpen(true)}>
                  <TrashIcon className="size-4" /> Delete selected
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div style={{ width: "90%" }}>
        {filteredRows.length > 0 ? (
          <div className="flex justify-center" style={{ overflowX: "auto" }}>
            <div style={{ width: "100%" }}>
              <DataTable
                columns={columns}
                data={pagedRows}
                emptyMessage="No secrets have been created."
                pagination={{
                  page,
                  pageSize: PAGE_SIZE,
                  total: filteredRows.length,
                  onPageChange: setPage,
                }}
                maxHeight="75vh"
              />
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <p>No secrets have been created.</p>
          </div>
        )}
      </div>

      {/* Bulk delete confirmation */}
      <Dialogbox
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        title="Delete selected secrets"
        severity="warning"
        message={
          <div>
            This will permanently delete {selectedNames.size} secret{selectedNames.size === 1 ? "" : "s"}:
            <br />
            {Array.from(selectedNames).join(", ")}
          </div>
        }
        onConfirm={handleBulkDelete}
        confirmText="Delete"
        closeText="Cancel"
        loading={bulkDeleteLoading}
      />

      {/* Bulk re-tag */}
      <BulkRetagDialog
        open={bulkRetagOpen}
        onClose={() => setBulkRetagOpen(false)}
        count={selectedNames.size}
        onConfirm={handleConfirmBulkRetag}
      />

      {/* Bulk clone to cluster */}
      <BulkCloneDialog
        open={bulkCloneOpen}
        onClose={() => setBulkCloneOpen(false)}
        count={selectedNames.size}
        defaultCluster={selectedCluster}
        userRole={roles}
        onConfirm={handleConfirmBulkClone}
      />

      {/* Bulk operation success */}
      <Dialogbox
        open={bulkResultOpen}
        onClose={() => setBulkResultOpen(false)}
        title="Done"
        severity="success"
        message={bulkResultMessage}
      />

      {/* Duplicate dialog */}
      <DuplicateSecretDialog
        open={!!duplicateTarget}
        onClose={() => setDuplicateTarget(null)}
        sourceName={duplicateTarget?.name}
        defaultCluster={selectedCluster}
        defaultEnvironment={getTagValue(duplicateTarget?.tags, "Environment")}
        defaultTeam={getTagValue(duplicateTarget?.tags, "Project")}
        userRole={roles}
        onConfirm={handleConfirmDuplicate}
      />

      {/* Duplicate success */}
      <Dialogbox
        open={!!duplicateSuccess}
        onClose={() => setDuplicateSuccess(null)}
        title="Secret duplicated"
        severity="success"
        message={
          <div>
            Created "{duplicateSuccess?.newName}" in cluster "{duplicateSuccess?.targetCluster}".
          </div>
        }
        onConfirm={handleViewDuplicate}
        confirmText="View duplicate"
        closeText="Stay here"
      />

      {/* Rename dialog */}
      <RenameSecretDialog
        open={!!renameTarget}
        onClose={() => setRenameTarget(null)}
        currentName={renameTarget?.name}
        onConfirm={handleConfirmRename}
        loading={renameLoading}
      />

      {/* Rename partial-failure warning */}
      <Dialogbox
        open={renamePartialOpen}
        onClose={() => setRenamePartialOpen(false)}
        title="Rename partially completed"
        severity="warning"
        message={renamePartialMessage}
      />

      {/* Generic action error */}
      <Dialogbox
        open={actionErrorOpen}
        onClose={() => setActionErrorOpen(false)}
        title="Error"
        severity="error"
        message={actionError}
      />
    </div>
  );
}

export default SecretList;
